// The spend report's shared plumbing: who may see it, which URL filters
// scope it, and how its queries retry. The breakdown cards
// (SpendAttribution.tsx) and the spend chart (SpendChart.tsx) both read it so
// they always describe the same scope.

import { ApiError, api, type UsageReportFilter } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { useMemo } from "react";

// Retry policy shared by every report query: 409 (brain predates the routes),
// 503 (brain unreachable) and 403 are stable states, not blips.
export const reportRetry = (count: number, err: unknown) =>
  !(
    err instanceof ApiError &&
    (err.status === 409 || err.status === 503 || err.status === 403)
  ) && count < 2;

// One active filter; the chip label is derived at render from the same maps
// the cards use, so a deep-linked URL labels itself.
export interface ActiveFilter {
  param: "userId" | "channelId" | "purpose" | "threadId";
  value: string;
}

// Who may see the breakdown (and the chart's split by purpose): viewers the
// backend grants viewReports (Boo admins and owners, platform operators) AND
// only for Boos inside the rollout — one access probe, cached. Anyone else,
// and any Boo outside the rollout, gets no section and no report requests.
// Everyone who sees the panel may scope it to one conversation; that view
// stays conversation-level (where, who, when, runs, cost) and never shows
// messages or per-turn rows. `wanted: false` asks nothing and answers "off".
export function useSpendAccess(containerId: string, wanted = true): "pending" | "on" | "off" {
  const containers = useQuery({
    queryKey: ["containers"],
    queryFn: api.containers.list,
    enabled: wanted,
  });
  const row = containers.data?.data.find((c) => c.id === containerId);
  const canViewReports = row?._permissions?.canViewReports === true;
  const access = useQuery({
    queryKey: ["usage-report-access", containerId],
    queryFn: () => api.containers.usage.report.access(containerId),
    enabled: wanted && canViewReports,
    retry: reportRetry,
    staleTime: 5 * 60 * 1000,
  });
  if (!wanted) return "off";
  if (containers.isLoading) return "pending";
  if (!canViewReports || access.isError) return "off";
  if (access.isLoading) return "pending";
  return access.data?.enabled === true ? "on" : "off";
}

// URL search-param name per filter dimension: panel state lives in the URL
// so browser Back/Forward step through filter changes.
export const FILTER_SEARCH_KEYS: Record<ActiveFilter["param"], string> = {
  userId: "uUser",
  channelId: "uChannel",
  purpose: "uPurpose",
  threadId: "uThread",
};

// The active filters, read from the URL.
export function useActiveFilters(): {
  filters: ActiveFilter[];
  filter: UsageReportFilter;
  searchString: (key: string) => string | undefined;
} {
  const search: Record<string, unknown> = useSearch({ strict: false });
  const searchString = (key: string): string | undefined => {
    const value = search[key];
    return typeof value === "string" && value.length > 0 ? value : undefined;
  };
  const filters = useMemo(() => {
    const out: ActiveFilter[] = [];
    for (const [param, key] of Object.entries(FILTER_SEARCH_KEYS) as Array<
      [ActiveFilter["param"], string]
    >) {
      const value = searchString(key);
      if (value !== undefined) out.push({ param, value });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- searchString is stable per `search`
  }, [search]);
  const filter = useMemo(() => {
    const out: UsageReportFilter = {};
    for (const f of filters) out[f.param] = f.value;
    return out;
  }, [filters]);
  return { filters, filter, searchString };
}
